import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Archive, Target } from 'lucide-react';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SettingsModalProvider } from './context/SettingsModalContext';
import SettingsModal from './components/Settings/SettingsModal';
import ProtectedRoute from './components/ProtectedRoute';
import ParentProtectedRoute from './components/ParentProtectedRoute';
import SessionExpiryHandler from './components/SessionExpiryHandler';
import StudentLayout from './components/StudentShell/StudentLayout';
import AdminLayout from './components/Admin/AdminLayout';
import PlaceholderPage from './pages/PlaceholderPage';
import Login from './pages/Login';

// Login is the anonymous first-load path (/ -> /login) and stays a static
// import; everything below is only reachable after auth, so it's
// code-split out of the landing-page bundle instead of shipping in one
// 700KB+ monolith that anonymous visitors never touch.
const Home = lazy(() => import('./pages/Home'));
const CurriculumMenuPage = lazy(() => import('./pages/CurriculumMenuPage'));
const QuizPortal = lazy(() => import('./pages/QuizPortal'));
const AdminPortal = lazy(() => import('./pages/AdminPortal'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AdminProfile = lazy(() => import('./pages/AdminProfile'));
const Registration = lazy(() => import('./pages/Registration'));
const StudentProfile = lazy(() => import('./pages/StudentProfile'));
const ProgressTracker = lazy(() => import('./pages/ProgressTracker'));
const PerformanceReports = lazy(() => import('./pages/PerformanceReports'));
const Assessments = lazy(() => import('./pages/Assessments'));
const MySyllabus = lazy(() => import('./pages/MySyllabus'));
const ConceptHub = lazy(() => import('./pages/ConceptHub'));
const Roadmap = lazy(() => import('./pages/Roadmap'));
const AdminStudents = lazy(() => import('./pages/AdminStudents'));
const AdminStaff = lazy(() => import('./pages/AdminStaff'));
const AdminRoles = lazy(() => import('./pages/AdminRoles'));
const AdminLexaAnalytics = lazy(() => import('./pages/AdminLexaAnalytics'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));
const StudentSupport = lazy(() => import('./pages/StudentSupport'));
const StudentFeedback = lazy(() => import('./pages/StudentFeedback'));
const MyAvailability = lazy(() => import('./pages/MyAvailability'));
const StudyPlan = lazy(() => import('./pages/StudyPlan'));
const MentorWorkspace = lazy(() => import('./pages/MentorWorkspace'));
const AdminStudentAvailability = lazy(() => import('./pages/AdminStudentAvailability'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const SupportDesk = lazy(() => import('./pages/SupportDesk'));
const OurTeam = lazy(() => import('./pages/OurTeam'));
const ParentChildSelect = lazy(() => import('./pages/ParentChildSelect'));
const ParentDashboard = lazy(() => import('./pages/ParentDashboard'));
const ParentVerifyLanding = lazy(() => import('./pages/ParentVerifyLanding'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';

// Shown while a lazy route's JS chunk is downloading. Matches every
// protected page's own bg-white (AdminPortal, AdminSettings, AdminProfile,
// Registration, StudentProfile, QuizPortal all use it; CurriculumMenuPage/
// Home default to light too) and ProtectedRoute's own loading screen below
// - this used to be the pre-rebrand dark navy console look, so a student
// landing on e.g. /practice saw dark slate (ProtectedRoute) -> dark navy
// (this fallback) -> white (the actual page) flash past in quick
// succession, reading as a "flicker" (docs/DECISIONS.md, "Bug-report
// batch: session/exam-flow/notification/curriculum fixes").
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="h-10 w-10 rounded-full border-2 border-brand-blue border-t-transparent animate-spin" />
    </div>
  );
}

export default function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <ThemeProvider>
        {/* Moved here from StudentLayout.jsx (docs/DECISIONS.md, 2026-08-25
            "Parent login..." decision) - AppHeader.jsx's Settings trigger
            is used by pages outside StudentLayout's subtree too
            (Registration.jsx, and now the Parent Login dashboard pages),
            so the provider needs to cover the whole app, not just that
            layout. SettingsModal itself is always mounted but only
            visually renders its panel when isOpen - safe to mount
            app-wide including on pages with no way to open it. */}
        <SettingsModalProvider>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <SessionExpiryHandler />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                {/* Public marketing page, reachable without auth like
                    /login itself (docs/DECISIONS.md, "Our Team page"). */}
                <Route path="/our-team" element={<OurTeam />} />
                <Route path="/register" element={
                  <ProtectedRoute>
                    <Registration />
                  </ProtectedRoute>
                } />

                {/* Parent Login (docs/DECISIONS.md, 2026-08-25 "Parent
                    login..." decision) - a parent isn't a student, so
                    none of these three routes nest under StudentLayout
                    below. /parent/verify is public (no auth guard) - the
                    24h token itself is the credential, same reasoning as
                    the backend route it calls. ParentProtectedRoute (not
                    the generic ProtectedRoute) additionally requires
                    accountType==='parent' on the other two. */}
                <Route path="/parent/verify" element={<ParentVerifyLanding />} />
                <Route path="/parent/select-child" element={
                  <ParentProtectedRoute>
                    <ParentChildSelect />
                  </ParentProtectedRoute>
                } />
                <Route path="/parent/dashboard/:studentId" element={
                  <ParentProtectedRoute>
                    <ParentDashboard />
                  </ParentProtectedRoute>
                } />

                {/* Every logged-in, non-admin route shares the persistent
                    left-side nav (docs/DECISIONS.md, "Persistent left-side
                    student navigation + light-theme visual fix") - a single
                    auth check + layout wrapping every child route below via
                    React Router's layout-route + <Outlet /> pattern. */}
                <Route element={
                  <ProtectedRoute>
                    <StudentLayout />
                  </ProtectedRoute>
                }>
                  <Route path="/practice" element={<CurriculumMenuPage />} />
                  <Route path="/student-profile" element={<StudentProfile />} />
                  <Route path="/progress" element={<ProgressTracker />} />
                  <Route path="/reports" element={<PerformanceReports />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/exam/:examId" element={<QuizPortal />} />
                  <Route path="/assessments" element={<Assessments />} />
                  <Route path="/my-syllabus" element={<MySyllabus />} />
                  {/* Per-chapter reference material - Notes/Video Explainer/
                      PPT/Activity Shorts/Mind Map/Flash Cards (docs/
                      DECISIONS.md, "New feature approved: Concept Hub"). */}
                  <Route path="/concept-hub" element={<ConceptHub />} />
                  <Route path="/support" element={<StudentSupport />} />
                  {/* Pilot user feedback (docs/DECISIONS.md, "New feature
                      approved: User Feedback"). */}
                  <Route path="/feedback" element={<StudentFeedback />} />
                  {/* "My Roadmap" renamed to "Learning Journey" - now a real
                      page, not a placeholder (docs/DECISIONS.md, "Sequential
                      concept/subconcept locking..." follow-up). */}
                  <Route path="/roadmap" element={<Roadmap />} />

                  {/* Real page now, not a placeholder (docs/DECISIONS.md,
                      "My Availability" decision). */}
                  <Route path="/availability" element={<MyAvailability />} />

                  {/* Real page now, not a placeholder (docs/DECISIONS.md,
                      2026-08-28 "Smart Study Plan - Phase 6"). */}
                  <Route path="/study-plan" element={<StudyPlan />} />
                  <Route path="/previous-year-questions" element={
                    <PlaceholderPage
                      title="Previous Year Papers"
                      icon={Archive}
                      description="Practice with real questions from past JEE/NEET papers."
                    />
                  } />
                  <Route path="/model-exams" element={
                    <PlaceholderPage
                      title="Mock Tests"
                      icon={Target}
                      description="Full-length, timed mock exams that simulate the real test experience."
                    />
                  } />
                </Route>

                {/* /curriculum renamed to /practice (docs/DECISIONS.md,
                    "Bug-report batch: session/exam-flow/notification/
                    curriculum fixes" follow-up) - self-practice vs.
                    admin-assigned assessments (exam configuration) were
                    easy to confuse under the old name. Kept as a redirect,
                    not removed outright, so any existing bookmark/browser
                    history still lands somewhere instead of a blank page -
                    there's no catch-all/NotFound route in this app today. */}
                <Route path="/curriculum" element={<Navigate to="/practice" replace />} />

                {/* Every admin page below shares one persistent header/
                    sidebar/footer shell (docs/DECISIONS.md, 2026-09-02
                    "Admin nav-bar layout refactor" - real report: "the left
                    nav-bar is not fixed... refreshing for every request").
                    Same layout-route + <Outlet /> pattern as StudentLayout
                    above - a single auth check + shell wrapping every child
                    route, so only each page's own content unmounts/
                    remounts on navigation, not the sidebar/header too. */}
                <Route element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }>
                  <Route path="/admin" element={<AdminPortal />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/students" element={<AdminStudents />} />
                  <Route path="/admin/staff" element={<AdminStaff />} />
                  <Route path="/admin/roles" element={<AdminRoles />} />
                  <Route path="/admin/lexa-analytics" element={<AdminLexaAnalytics />} />
                  <Route path="/admin/feedback" element={<AdminFeedback />} />
                  <Route path="/admin/student-availability" element={<AdminStudentAvailability />} />
                </Route>

                <Route path="/admin/profile" element={
                  <ProtectedRoute>
                    <AdminProfile />
                  </ProtectedRoute>
                } />
                <Route path="/admin/mentor" element={
                  <ProtectedRoute>
                    <MentorWorkspace />
                  </ProtectedRoute>
                } />
                <Route path="/admin/manager" element={
                  <ProtectedRoute>
                    <ManagerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/admin/support" element={
                  <ProtectedRoute>
                    <SupportDesk />
                  </ProtectedRoute>
                } />
                <Route path="/" element={<Navigate to="/login" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <SettingsModal />
        </SettingsModalProvider>
        </ThemeProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}
